import { createTheme, responsiveFontSizes }  from "@mui/material/styles";
import { Shadows } from "@mui/material/styles/shadows";
import { darkColors, lightColors } from "./colors";
import { fonts } from "./fonts";

export const theme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      dark: darkColors.background.brand.dark,
      main: darkColors.background.brand.mid,
      light: darkColors.background.brand.lighter,
      contrastText: darkColors.background.brand.light,
    },
    secondary: {
      main: darkColors.background.neutral.dark,
      dark: darkColors.background.neutral.darker,
      light: darkColors.background.neutral.mid,
      contrastText: darkColors.background.neutral.light,
    },
    text: {
      primary: darkColors.text.brand.darkest,
      secondary: darkColors.text.brand.darker,
      disabled: darkColors.text.neutral.dark,
    },
    background: {
      default: darkColors.background.brand.lightest,
      paper: darkColors.background.brand.lightest,
    },
    divider: darkColors.border.brand.light,
    common: {
      black: darkColors.background.neutral.darkest,
      white: darkColors.background.neutral.lightest,
    }
  },
  typography: {
    fontFamily: fonts.families.primary,
    h1: {
      ...fonts.semantic.h1,
    },
    h2: {
      ...fonts.semantic.h2,
    },
    h3: {
      ...fonts.semantic.h3,
    },
    h4: {
      ...fonts.semantic.h4,
    },
    h5: {
      ...fonts.semantic.h5,
    },
    h6: {
      ...fonts.semantic.h6,
    },
    body1: {
      ...fonts.semantic.body,
    },
    body2: {
      ...fonts.semantic.bodySmall,
    },
    button: {
      ...fonts.semantic.button,
    },
    caption: {
      ...fonts.semantic.caption,
    },
    overline: {
      ...fonts.semantic.label,
    },
  },
  components: {
    MuiTypography: {
      styleOverrides: {
        root: {
          color: darkColors.text.brand.darkest,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: darkColors.background.brand.lightest,
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          ...fonts.semantic.button,
        },
        // Contained button variants
        contained: {
          color: darkColors.text.brand.darkest,
          '&:hover': {
            backgroundColor: darkColors.background.brand.dark,
          },
        },
        containedPrimary: {
          backgroundColor: darkColors.background.brand.mid,
          '&:hover': {
            backgroundColor: darkColors.background.brand.dark,
          },
        },
        containedSecondary: {
          backgroundColor: darkColors.background.brand.mid,
          '&:hover': {
            backgroundColor: darkColors.background.brand.dark,
          },
        },
        // Outlined button variants
        outlined: {
          backgroundColor: 'transparent',
          borderColor: darkColors.background.brand.mid,
          color: darkColors.text.brand.mid,
          '&:hover': {
            backgroundColor: darkColors.background.brand.dark,
            borderColor: darkColors.border.brand.dark,
          },
        },
        outlinedPrimary: {
          color: darkColors.text.brand.darkest,
          borderColor: darkColors.border.brand.mid,
          '&:hover': {
            backgroundColor: darkColors.background.brand.dark,
            borderColor: darkColors.border.brand.dark,
            color: darkColors.text.brand.darker,
          },
        },
        outlinedSecondary: {
          color: darkColors.text.neutral.darker,
          borderColor: darkColors.border.neutral.dark,
          '&:hover': {
            backgroundColor: darkColors.background.neutral.dark,
            borderColor: darkColors.border.neutral.dark,
            color: darkColors.text.neutral.darker,
          },
        },
        // Text button variants
        text: {
          color: darkColors.text.brand.darkest,
          '&:hover': {
            backgroundColor: darkColors.background.brand.dark,
          },
        },
        textPrimary: {
          color: darkColors.background.brand.mid,
          '&:hover': {
            backgroundColor: darkColors.background.brand.dark + '15', // 15% opacity
            color: darkColors.text.brand.darker,
          },
        },
        textSecondary: {
          color: darkColors.background.neutral.mid,
          '&:hover': {
            backgroundColor: darkColors.background.neutral.dark + '15', // 15% opacity
            color: darkColors.text.neutral.darker,
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        }
      }
    }
  },
  shadows: Array(25).fill("none") as Shadows,
});

export const lightTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      dark: lightColors.background.brand.dark,
      main: lightColors.background.brand.mid,
      light: lightColors.background.brand.lighter,
      contrastText: lightColors.background.brand.light,
    },
    secondary: {
      main: lightColors.background.neutral.dark,
      dark: lightColors.background.neutral.darker,
      light: lightColors.background.neutral.mid,
      contrastText: lightColors.background.neutral.light,
    },
    text: {
      primary: lightColors.text.brand.darkest,
      secondary: lightColors.text.brand.darker,
      disabled: lightColors.text.neutral.dark,
    },
    background: {
      default: lightColors.background.brand.lightest,
      paper: lightColors.background.brand.lightest,
    },
    divider: lightColors.border.brand.light,
    common: {
      black: lightColors.background.neutral.darkest,
      white: lightColors.background.neutral.lightest,
    }
  },
  typography: {
    fontFamily: fonts.families.primary,
    h1: {
      ...fonts.semantic.h1,
    },
    h2: {
      ...fonts.semantic.h2,
    },
    h3: {
      ...fonts.semantic.h3,
    },
    h4: {
      ...fonts.semantic.h4,
    },
    h5: {
      ...fonts.semantic.h5,
    },
    h6: {
      ...fonts.semantic.h6,
    },
    body1: {
      ...fonts.semantic.body,
    },
    body2: {
      ...fonts.semantic.bodySmall,
    },
    button: {
      ...fonts.semantic.button,
    },
    caption: {
      ...fonts.semantic.caption,
    },
    overline: {
      ...fonts.semantic.label,
    },
  },
  components: {
    MuiTypography: {
      styleOverrides: {
        root: {
          color: lightColors.text.brand.darkest,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: lightColors.background.brand.lightest,
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          ...fonts.semantic.button,
        },
        // Contained button variants
        contained: {
          color: lightColors.text.brand.darkest,
          '&:hover': {
            backgroundColor: lightColors.background.brand.light,
          },
        },
        containedPrimary: {
          backgroundColor: lightColors.background.brand.lighter,
          '&:hover': {
            backgroundColor: lightColors.background.brand.light,
          },
        },
        containedSecondary: {
          backgroundColor: lightColors.background.brand.lighter,
          '&:hover': {
            backgroundColor: lightColors.background.brand.dark,
          },
        },
        // Outlined button variants
        outlined: {
          backgroundColor: 'transparent',
          borderColor: lightColors.background.brand.lighter,
          color: lightColors.text.brand.mid,
          '&:hover': {
            backgroundColor: lightColors.background.brand.light,
            borderColor: lightColors.border.brand.dark,
          },
        },
        outlinedPrimary: {
          color: lightColors.text.brand.darkest,
          borderColor: lightColors.border.brand.mid,
          '&:hover': {
            backgroundColor: lightColors.background.brand.light,
            borderColor: lightColors.border.brand.dark,
            color: lightColors.text.brand.darker,
          },
        },
        outlinedSecondary: {
          color: lightColors.text.neutral.darker,
          borderColor: lightColors.border.neutral.dark,
          backgroundColor: lightColors.border.neutral.lighter,
          '&:hover': {
            backgroundColor: lightColors.background.neutral.light,
            borderColor: lightColors.border.neutral.dark,
            color: lightColors.text.neutral.darker,
          },
        },
        // Text button variants
        text: {
          color: lightColors.text.brand.darkest,
          '&:hover': {
            backgroundColor: lightColors.background.brand.dark,
          },
        },
        textPrimary: {
          color: lightColors.background.brand.mid,
          '&:hover': {
            backgroundColor: lightColors.background.brand.dark + '15', // 15% opacity
            color: lightColors.text.brand.darker,
          },
        },
        textSecondary: {
          color: lightColors.background.neutral.mid,
          '&:hover': {
            backgroundColor: lightColors.background.neutral.dark + '15', // 15% opacity
            color: lightColors.text.neutral.darker,
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        }
      }
    }
  },
  shadows: Array(25).fill("none") as Shadows,
});

// Apply responsive font sizes to both themes
const responsiveDarkTheme = responsiveFontSizes(theme);
const responsiveLightTheme = responsiveFontSizes(lightTheme);

// Export the responsive dark theme as default
export default responsiveDarkTheme;

// Export both responsive themes for use with theme toggle
export { responsiveDarkTheme, responsiveLightTheme };
